// ======================
// IncludeAllSystemsStep
// ======================

import { useMigrateOpsThinDataReportWizardState } from "./MigrateOpsThinDataReportWizardState";
import { Alert, AlertTitle, Box, Checkbox, LinearProgress, Stack, Typography } from "@mui/material";
import React, { useState } from "react";
import { useStepperContext } from "../../../../common/stepper/StepperState";
import { SlideAndFadeInStep, StepperNavButtons } from "../../../../common/stepper/StepperComponents";
import { SelectableCard } from "../../../../common/card/SelectableCard";
import MinimumMode from "../../../../assets/migrationSession/minimum.png";
import ModerateMode from "../../../../assets/migrationSession/moderate.png";
import AggressiveMode from "../../../../assets/migrationSession/aggressive.png";
import RelentlessMode from "../../../../assets/migrationSession/relentless.png";
import { useLinearProgressStyle } from "../../../cmcMigration/CmcMigrationCommon";
import { useListGalaxyMigrateDeployments } from "../../../deployment/deployment_hooks";
import { useCurrentProjectID } from "../../../project/CurrentProjectState";
import { createColumnHelper, PaginationState } from "@tanstack/react-table";
import { GalaxyMigrateDeploymentInfo } from "gc-web-proto/galaxycompletepb/apipb/domainpb/galaxymigrate_pb";
import { BlackTooltip } from "../../../../common/tooltip/ColorTooltip";
import { parseCpuCountsFromCpuString } from "../../../deployment/DeploymentCommon";
import { formatKnownDataType, KnownDataType } from "../../../../common/utils/formatter";
import { getResourceUsageColor, HostInfoDisplay } from "../../../galaxymigrate/GalaxyMigrateDeploymentsList";
import { QueryTable } from "../../../../common/table/QueryTable";
import { DeploymentQueryKeys } from "../../../../common/QueryKeys";
import { ViewCurrentYamlButton } from "../MigrateOpsWizardCommon";
import { ThinDataReportWizardStepId } from "./MigrateOpsThinDataReportWizard";
import { OperationRecipeID } from "gc-web-proto/galaxycompletepb/operationpb/operation_pb";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";

export const IncludeAllSystemsStep: React.FC = () => {
    const { t } = useTranslation("migrateops");
    const wizardState = useMigrateOpsThinDataReportWizardState();
    const { operationJson, setOperationJsonField } = wizardState;
    const activeStep = useStepperContext((s) => s.activeStep);
    const stepConfigs = useStepperContext((s) => s.stepConfigs);
    const includeAllSystems = operationJson.config.migrateops_migration_source_assessment_report.include_all_systems_in_project;
    const setIncludeAllSystems = (includeAllSystems: boolean) => {
        setOperationJsonField(["config", "migrateops_migration_source_assessment_report", "include_all_systems_in_project"], includeAllSystems);
    };

    const clearAllSystemNames = () => {
        setOperationJsonField(["config", "migrateops_migration_source_assessment_report", "include_system_names"], []);
        setOperationJsonField(["config", "migrateops_migration_source_assessment_report", "exclude_system_names"], []);
    };

    const id = ThinDataReportWizardStepId.INCLUDE_ALL_SYSTEMS;

    return (
        <SlideAndFadeInStep transitionIn={stepConfigs[activeStep].id === id}>
            <Box>
                <Stack textAlign={"center"} direction={"column"} spacing={6} alignItems={"center"} justifyContent={"center"} pt={4} pb={6}>
                    <Typography variant={"h4"}>{t("wizard.thinDataReport.includeAll.question")}</Typography>
                    <Stack direction={"row"} justifyContent={"space-between"} spacing={2}>
                        <SelectableCard
                            background={"light"}
                            selected={includeAllSystems === true}
                            onSelect={() => {
                                setIncludeAllSystems(true);
                                clearAllSystemNames();
                            }}
                            cardProps={{ sx: { width: 300 } }}
                            title={t("common:actions.yes")}
                            description={t("wizard.thinDataReport.includeAll.yesDescription")}
                        />
                        <SelectableCard
                            background={"light"}
                            selected={includeAllSystems === false}
                            onSelect={() => {
                                setIncludeAllSystems(false);
                            }}
                            cardProps={{ sx: { width: 300 } }}
                            title={t("common:actions.no")}
                            description={t("wizard.thinDataReport.includeAll.noDescription")}
                        />
                    </Stack>
                </Stack>
                <StepperNavButtons
                    nextButtonProps={{
                        disabled: includeAllSystems === undefined,
                    }}
                />
                <Stack direction={"row"} justifyContent={"center"} pt={2}>
                    <ViewCurrentYamlButton operationJson={operationJson} recipeId={OperationRecipeID.MIGRATEOPS_MIGRATION_SOURCE_ASSESSMENT_REPORT} />
                </Stack>
            </Box>
        </SlideAndFadeInStep>
    );
};

// ======================
// SelectSystemsStep
// ======================

export const SelectSystemsStep: React.FC = () => {
    const { t } = useTranslation("migrateops");
    const wizardState = useMigrateOpsThinDataReportWizardState();
    const { operationJson } = wizardState;

    const activeStep = useStepperContext((s) => s.activeStep);
    const stepConfigs = useStepperContext((s) => s.stepConfigs);

    const id = ThinDataReportWizardStepId.SELECT_SYSTEMS;
    return (
        <SlideAndFadeInStep transitionIn={stepConfigs[activeStep].id === id}>
            <Box>
                <Stack textAlign={"center"} pt={4} pb={6} direction={"column"} spacing={6} alignItems={"center"} justifyContent={"center"}>
                    <Typography variant={"h4"}>{t("wizard.thinDataReport.systems.title")}</Typography>
                    <SelectSystemsTable />
                    <Box textAlign={"left"} width={"100%"}>
                        <Alert severity={"info"}>
                            <AlertTitle>{t("wizard.thinDataReport.systems.excludingTitle")}</AlertTitle>
                            {t("wizard.thinDataReport.systems.excludingHelp")}
                        </Alert>
                    </Box>
                    <Box textAlign={"left"} width={"100%"}>
                        <Alert severity={"info"}>
                            <AlertTitle>{t("wizard.thinDataReport.systems.advancedTitle")}</AlertTitle>
                            {t("wizard.thinDataReport.systems.advancedHelp")}
                        </Alert>
                    </Box>
                </Stack>
                <StepperNavButtons />
                <Stack direction={"row"} justifyContent={"center"} pt={2}>
                    <ViewCurrentYamlButton operationJson={operationJson} recipeId={OperationRecipeID.MIGRATEOPS_MIGRATION_SOURCE_ASSESSMENT_REPORT} />
                </Stack>
            </Box>
        </SlideAndFadeInStep>
    );
};

// ======================
// SelectSystemsTable
// ======================

interface SelectSystemsTableProps {
    includeSystems?: boolean;
}

export const SelectSystemsTable: React.FC<SelectSystemsTableProps> = (p) => {
    const { t } = useTranslation("migrateops");
    const wizardState = useMigrateOpsThinDataReportWizardState();
    const { operationJson, setOperationJsonField, includeSystems, setIncludeSystems } = wizardState;
    const includedSystems = operationJson.config.migrateops_migration_source_assessment_report.include_system_names;
    const excludedSystems = operationJson.config.migrateops_migration_source_assessment_report.exclude_system_names;
    const toggleSystemToInclude = (name: string) => {
        if (includeSystems) {
            setOperationJsonField(
                ["config", "migrateops_migration_source_assessment_report", "include_system_names"],
                includedSystems.includes(name) ? includedSystems.filter((n) => n !== name) : [...includedSystems, name]
            );
        } else {
            setOperationJsonField(
                ["config", "migrateops_migration_source_assessment_report", "exclude_system_names"],
                excludedSystems.includes(name) ? excludedSystems.filter((n) => n !== name) : [...excludedSystems, name]
            );
        }
    };

    const isSystemAdded = (name: string) => {
        return includeSystems ? includedSystems.includes(name) : excludedSystems.includes(name);
    };

    const projectId = useCurrentProjectID();
    const progressStyle = useLinearProgressStyle(true);

    const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
        pageIndex: 1,
        pageSize: 30,
    });

    const onlineSystems = useListGalaxyMigrateDeployments(projectId, true, pageIndex, pageSize);

    const columnHelper = createColumnHelper<GalaxyMigrateDeploymentInfo.AsObject>();

    const cols = [
        columnHelper.display({
            id: "select",
            cell: (props) => {
                const disabled = includeSystems === undefined;
                const selected = isSystemAdded(props.row.original.deployment.systemName);
                const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                    toggleSystemToInclude(props.row.original.deployment.systemName);
                };
                return (
                    <Checkbox
                        inputProps={{ "aria-label": t("wizard.thinDataReport.systems.selectSystem", { name: props.row.original.deployment.systemName }) }}
                        disabled={disabled}
                        color={"secondary"}
                        checked={selected}
                        onChange={onChange}
                    />
                );
            },
        }),
        columnHelper.accessor((c) => c, {
            id: "host",
            header: t("galaxymigrate:deployments.columns.host"),
            cell: (props) => {
                const deploymentInfo = props.row.original;
                return <HostInfoDisplay deploymentInfo={deploymentInfo} />;
            },
        }),
        columnHelper.accessor((c) => c, {
            id: "resource",
            header: t("galaxymigrate:deployments.columns.resourceUsage"),
            meta: {
                style: {
                    textAlign: "center",
                },
            },
            cell: (props) => {
                const deploymentInfo = props.row.original;
                const hasCpuPercentage = !!deploymentInfo.deployment.systemUptime?.seconds;

                const memoryUsagePercentage = (deploymentInfo.memoryUsed / deploymentInfo.memory) * 100;
                return (
                    <Stack
                        minWidth={250}
                        alignItems={"flexStart"}
                        sx={{
                            opacity: deploymentInfo.deployment.connected ? 1 : 0.3,
                        }}
                    >
                        {hasCpuPercentage ? (
                            <BlackTooltip
                                title={
                                    <Box>
                                        <Typography variant={"caption"} fontWeight={600}>
                                            {deploymentInfo.cpu}
                                        </Typography>
                                        <br />
                                        <Typography variant={"caption"} color={"textSecondary"}>
                                            {t("galaxymigrate:deployments.resource.cpuModel")}
                                        </Typography>
                                    </Box>
                                }
                            >
                                <Stack direction={"row"} spacing={1} alignItems={"flex-end"}>
                                    <Typography variant={"caption"}>{t("galaxymigrate:details.fields.cpu")}&nbsp;</Typography>
                                    <Stack>
                                        <Stack direction={"row"} spacing={1} justifyContent={"space-between"}>
                                            <Typography variant={"caption"}>
                                                {t("galaxymigrate:deployments.resource.cores", {
                                                    count: parseCpuCountsFromCpuString(deploymentInfo.cpu).cores,
                                                })}
                                            </Typography>
                                            <Typography variant={"caption"} color={`${getResourceUsageColor(deploymentInfo.cpuUsagePercentage)}.main`}>
                                                {formatKnownDataType(deploymentInfo.cpuUsagePercentage, KnownDataType.PERCENT)}
                                            </Typography>
                                        </Stack>
                                        <LinearProgress
                                            sx={{
                                                ...progressStyle,
                                                minWidth: 200,
                                            }}
                                            variant={"determinate"}
                                            value={deploymentInfo.cpuUsagePercentage}
                                            color={getResourceUsageColor(deploymentInfo.cpuUsagePercentage)}
                                        />
                                    </Stack>
                                </Stack>
                            </BlackTooltip>
                        ) : null}
                        {deploymentInfo.memoryUsed >= 0 && (
                            <Box pt={1}>
                                <Stack alignItems={"flex-start"} direction={"row"} spacing={1}>
                                    <Typography variant={"caption"}>{t("galaxymigrate:deployments.resource.memoryAbbreviation")}</Typography>
                                    <Stack>
                                        <LinearProgress
                                            sx={{
                                                ...progressStyle,
                                                minWidth: 200,
                                            }}
                                            variant={"determinate"}
                                            value={memoryUsagePercentage}
                                            color={getResourceUsageColor(memoryUsagePercentage)}
                                        />
                                        <Stack direction={"row"} justifyContent={"space-between"}>
                                            <Typography variant={"caption"}>
                                                {`${formatKnownDataType(deploymentInfo.memoryUsed, KnownDataType.CAPACITY)}/${formatKnownDataType(
                                                    deploymentInfo.memory,
                                                    KnownDataType.CAPACITY
                                                )}`}
                                            </Typography>
                                            <Typography variant={"caption"} color={`${getResourceUsageColor(memoryUsagePercentage)}.main`}>
                                                {formatKnownDataType(memoryUsagePercentage, KnownDataType.PERCENT)}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </Box>
                        )}
                    </Stack>
                );
            },
        }),
    ];

    return (
        <Box pt={0} width={"100%"}>
            <QueryTable
                data={onlineSystems.data?.itemsList}
                columns={cols}
                emptyTableConfig={{}}
                error={onlineSystems.error}
                isLoading={onlineSystems.isLoading}
                pageCount={onlineSystems.data?.pagerMeta?.totalPages || 0}
                totalItems={onlineSystems.data?.pagerMeta?.totalItems}
                itemTypeLabel={t("galaxymigrate:deployments.columns.host")}
                pagination={{ pageIndex, pageSize }}
                setPagination={setPagination}
                refetch={onlineSystems.refetch}
                queryKey={DeploymentQueryKeys.listGmDeployments}
            />
        </Box>
    );
};

// ======================
// ExcludeBootVolumesStep
// ======================

export const ExcludeBootVolumesStep: React.FC = () => {
    const { t } = useTranslation("migrateops");
    const wizardState = useMigrateOpsThinDataReportWizardState();
    const { operationJson, setOperationJsonField } = wizardState;
    const activeStep = useStepperContext((s) => s.activeStep);
    const stepConfigs = useStepperContext((s) => s.stepConfigs);

    const excludeBootVolumes = operationJson.config.migrateops_migration_source_assessment_report.exclude_boot_volumes;
    const setExcludeBootVolumes = (excludeBootVolumes: boolean) => {
        setOperationJsonField(["config", "migrateops_migration_source_assessment_report", "exclude_boot_volumes"], excludeBootVolumes);
    };
    const id = ThinDataReportWizardStepId.EXCLUDE_BOOT_VOLUMES;

    return (
        <SlideAndFadeInStep transitionIn={stepConfigs[activeStep].id === id}>
            <Box>
                <Stack textAlign={"center"} pt={4} pb={6} direction={"column"} spacing={6} alignItems={"center"} justifyContent={"center"}>
                    <Typography variant={"h4"}>{t("wizard.thinDataReport.bootVolumes.question")}</Typography>
                    <Stack direction={"row"} justifyContent={"space-between"} spacing={2}>
                        <SelectableCard
                            background={"light"}
                            selected={excludeBootVolumes === false}
                            title={t("wizard.thinDataReport.bootVolumes.include")}
                            onSelect={() => setExcludeBootVolumes(false)}
                            description={t("wizard.thinDataReport.bootVolumes.includeDescription")}
                        />
                        <SelectableCard
                            background={"light"}
                            selected={excludeBootVolumes === true}
                            onSelect={() => setExcludeBootVolumes(true)}
                            title={t("wizard.thinDataReport.bootVolumes.exclude")}
                            description={t("wizard.thinDataReport.bootVolumes.excludeDescription")}
                        />
                    </Stack>
                </Stack>
                <StepperNavButtons
                    nextButtonProps={{
                        disabled: excludeBootVolumes === undefined,
                    }}
                />
                <Stack direction={"row"} justifyContent={"center"} pt={2}>
                    <ViewCurrentYamlButton operationJson={operationJson} recipeId={OperationRecipeID.MIGRATEOPS_MIGRATION_SOURCE_ASSESSMENT_REPORT} />
                </Stack>
            </Box>
        </SlideAndFadeInStep>
    );
};

// ======================
// DefaultQosLevelStep
// ======================

export const DefaultQosLevelStep: React.FC = () => {
    const { t } = useTranslation("migrateops");
    const wizardState = useMigrateOpsThinDataReportWizardState();
    const { operationJson, setOperationJsonField } = wizardState;
    const activeStep = useStepperContext((s) => s.activeStep);
    const stepConfigs = useStepperContext((s) => s.stepConfigs);

    const defaultQosLevel = operationJson.config.migrateops_migration_source_assessment_report.default_qos_level;
    const setDefaultQosLevel = (defaultQosLevel: string) => {
        setOperationJsonField(["config", "migrateops_migration_source_assessment_report", "default_qos_level"], defaultQosLevel);
    };

    const id = ThinDataReportWizardStepId.DEFAULT_QOS_LEVEL;

    const qosLevels = [
        {
            title: t("cmcMigration:qos.impact.minimum"),
            description: t("cmcMigration:qos.options.minimumDescription"),
            icon: <img src={MinimumMode} height={72} alt={t("cmcMigration:qos.options.minimumImageAlt")} />,
            value: `MINIMUM`,
        },
        {
            title: t("cmcMigration:qos.impact.moderate"),
            description: t("cmcMigration:qos.options.moderateDescription"),
            icon: <img src={ModerateMode} height={72} alt={t("cmcMigration:qos.options.moderateImageAlt")} />,
            value: `MODERATE`,
        },
        {
            title: t("cmcMigration:qos.impact.aggressive"),
            description: t("cmcMigration:qos.options.aggressiveDescription"),
            icon: <img src={AggressiveMode} height={72} alt={t("cmcMigration:qos.options.aggressiveImageAlt")} />,
            value: `AGGRESSIVE`,
        },
        {
            title: t("cmcMigration:qos.impact.relentless"),
            description: t("cmcMigration:qos.options.relentlessDescription"),
            icon: <img src={RelentlessMode} height={72} alt={t("cmcMigration:qos.options.relentlessImageAlt")} />,
            value: `RELENTLESS`,
        },
    ];

    return (
        <SlideAndFadeInStep transitionIn={stepConfigs[activeStep].id === id}>
            <Box>
                <Stack textAlign={"center"} pt={4} pb={6} direction={"column"} spacing={6} alignItems={"center"} justifyContent={"center"}>
                    <Typography variant={"h4"}>{t("wizard.thinDataReport.qos.title")}</Typography>
                    <Grid container spacing={2} p={2}>
                        {qosLevels.map((qosLevel, index) => {
                            return (
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6,
                                        md: 3,
                                    }}
                                >
                                    <SelectableCard
                                        background={"light"}
                                        cardProps={{ sx: { height: "100%" } }}
                                        key={index}
                                        selected={defaultQosLevel === qosLevel.value}
                                        onSelect={() => setDefaultQosLevel(qosLevel.value)}
                                        title={qosLevel.title}
                                        description={qosLevel.description}
                                        icon={qosLevel.icon}
                                    />
                                </Grid>
                            );
                        })}
                    </Grid>
                </Stack>
                <StepperNavButtons
                    nextButtonProps={{
                        disabled: defaultQosLevel === undefined,
                    }}
                />
                <Stack direction={"row"} justifyContent={"center"} pt={2}>
                    <ViewCurrentYamlButton operationJson={operationJson} recipeId={OperationRecipeID.MIGRATEOPS_MIGRATION_SOURCE_ASSESSMENT_REPORT} />
                </Stack>
            </Box>
        </SlideAndFadeInStep>
    );
};
